import { useCallback } from 'react'
import {
  mapFilterStateToGetScanFindingsParams,
  type FilterStateStoreValues,
} from '../components/tables/filter-state-store.tsx'
import { useToastService } from '../services/toast-service.tsx'
import { useExportScanFindingsCsv } from '../utils/api-client/api-hooks.ts'
import { type FindingSortField, type SortOrder } from '../utils/api-client/api-schemas.ts'
import { useShowMutationError } from './use-show-mutation-error.tsx'

type UseExportCsvParams = {
  scanId: string | undefined
  filterState: FilterStateStoreValues
  search?: string
  sort?: FindingSortField
  order?: SortOrder
}

type ToastContext = { toastId: string }

export function useExportCsv({ scanId, filterState, search, sort, order }: UseExportCsvParams) {
  const { addToast, removeToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Export failed' })

  const mutation = useExportScanFindingsCsv<ToastContext>({
    onMutate: () => {
      const toast = addToast({
        message: <>Export in progress</>,
        variant: 'loading',
      })
      return { toastId: toast.id }
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.toastId) {
        removeToast(context.toastId)
      }
    },
    onSuccess: () =>
      addToast({
        message: <>Export complete. Check your Downloads folder.</>,
        variant: 'success',
      }),
    onError: showMutationError,
  })

  const handleExportCsv = useCallback(() => {
    if (!scanId) return
    mutation.mutate({
      scanId,
      q: search,
      sort,
      order,
      ...mapFilterStateToGetScanFindingsParams(filterState),
    })
  }, [scanId, filterState, search, sort, order, mutation])

  return { handleExportCsv, isExportingCsv: mutation.isPending }
}
